/*
<purpose>
  Per-rep read-state: tracks which ticket uuids the authenticated rep has opened.
  Loads once on mount from GET /tickets/reads; markRead is optimistic (local Set
  updated immediately, POST fires-and-forgets so the dot disappears without waiting
  for a round-trip).
</purpose>
<dependencies>smarttix.getTicketReads / smarttix.markTicketRead (services/api.ts)</dependencies>
<constraints>
  - Read-set is loaded once; no polling. Re-mount reloads.
  - markRead is idempotent: if the uuid is already in the set the POST is skipped.
  - A rejected POST keeps the optimistic state — the read indicator must not flash
    back (failure is silent, logged only).
  - No customer param — per-customer DB means the endpoint is tenant-scoped by the FA.
</constraints>
*/
import { useState, useEffect, useCallback, useRef } from 'react';
import { smarttix } from '../services/api';

export interface UseReadState {
  isRead: (uuid: string) => boolean;
  markRead: (uuid: string) => void;
  loaded: boolean;
}

export function useReadState(): UseReadState {
  const [readSet, setReadSet] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  // Why: ref so markRead callback always sees the latest set without needing to
  // re-create itself on every render (avoids stale-closure without adding deps).
  const readSetRef = useRef(readSet);
  readSetRef.current = readSet;

  useEffect(() => {
    let cancelled = false;
    smarttix.getTicketReads().then(({ readUuids }) => {
      if (cancelled) return;
      setReadSet(new Set(readUuids));
      setLoaded(true);
    }).catch((err) => {
      if (cancelled) return;
      console.error('[useReadState] failed to load read-set', err);
      setLoaded(true); // still flip loaded so callers don't spin forever
    });
    return () => { cancelled = true; };
  }, []);

  const isRead = useCallback((uuid: string): boolean => {
    return readSetRef.current.has(uuid);
  }, []);

  const markRead = useCallback((uuid: string): void => {
    if (readSetRef.current.has(uuid)) return; // idempotent — skip redundant POST
    // Optimistic: add immediately so the unread indicator disappears at once.
    setReadSet(prev => {
      const next = new Set(prev);
      next.add(uuid);
      return next;
    });
    smarttix.markTicketRead(uuid).catch((err) => {
      // Intentionally keep optimistic state on failure — a flashing read indicator
      // is more disruptive than a missed server write. Log for diagnostics only.
      console.error('[useReadState] markTicketRead failed (keeping optimistic state)', err);
    });
  }, []);

  return { isRead, markRead, loaded };
}
